/**
 * Charts, the parts without React: each series' color and line style, nice axis ticks, the scales a chart draws with,
 * and what a hover over a line or a bar group shows.
 *
 * Color does one job per series kind (validated with the dataviz palette checks):
 * - versions are ordinal, so one blue ramp, the newest darkest: the series the chart is about;
 * - comparisons (the field's leaders) are context: neutral gray, dashed;
 * - other named groups are categorical, in a fixed order (three slots, validated together).
 */

const MAX_TICKS = 100

export interface ChartSeries {
  name: string
  /** What it is; sets its color. Default `group`. */
  kind?: 'version' | 'comparison' | 'group'
  /** For versions: orders the ramp. */
  version?: number | null
  /** How many games (or samples) it's from. */
  n?: number | null
  points: { x: number | string; y: number }[]
}

export interface ChartData {
  title?: string
  chart: 'line' | 'bar'
  x: { label: string; unit?: string }
  y: { label: string; unit?: string }
  series: ChartSeries[]
}

/** Light to dark: the newest version takes the darkest step. */
export const VERSION_RAMP = ['#86b6ef', '#3987e5', '#1c5cab', '#1a3875'] as const
export const COMPARISON = '#707070'
export const GROUPS = ['#2a78d6', '#eb6834', '#1baf7a'] as const

export interface SeriesStyle {
  color: string
  dashed: boolean
  /** The series the chart is about (the newest version): drawn on top, labelled at its end. */
  lead: boolean
}

/** Each series' style, by what it is, never by where it sits in the list. */
export function seriesStyles(series: readonly ChartSeries[]): Map<string, SeriesStyle> {
  const versions = series.filter((s) => s.kind === 'version').sort((a, b) => (a.version ?? 0) - (b.version ?? 0))
  const newest = versions.at(-1)?.name ?? null
  const out = new Map<string, SeriesStyle>()
  versions.forEach((s, i) => {
    const step = VERSION_RAMP.length - (versions.length - i)
    out.set(s.name, { color: VERSION_RAMP[Math.max(0, step)]!, dashed: false, lead: s.name === newest })
  })
  let group = 0
  for (const s of series) {
    if (s.kind === 'comparison') out.set(s.name, { color: COMPARISON, dashed: true, lead: false })
    else if ((s.kind ?? 'group') === 'group')
      out.set(s.name, {
        color: GROUPS[group++] ?? COMPARISON,
        dashed: group > GROUPS.length,
        lead: !newest && group === 1,
      })
  }
  return out
}

/** How many versions a chart draws: one ramp step each. */
export const VERSIONS_DRAWN = VERSION_RAMP.length

/** The series a chart draws: every comparison and group, and the newest versions (the rest are in its table). */
export function drawnSeries(series: readonly ChartSeries[]): { drawn: ChartSeries[]; hidden: number } {
  const versions = series.filter((s) => s.kind === 'version').sort((a, b) => (b.version ?? 0) - (a.version ?? 0))
  const keep = new Set(versions.slice(0, VERSIONS_DRAWN).map((s) => s.name))
  const drawn = series.filter((s) => s.kind !== 'version' || keep.has(s.name))
  return { drawn, hidden: series.length - drawn.length }
}

/** The order series draw in: context first, the lead last (on top). */
export function drawOrder(series: readonly ChartSeries[], styles: Map<string, SeriesStyle>): ChartSeries[] {
  const rank = (s: ChartSeries) => (styles.get(s.name)?.lead ? 2 : s.kind === 'comparison' ? 0 : 1)
  return [...series].sort((a, b) => rank(a) - rank(b) || (a.version ?? 0) - (b.version ?? 0))
}

/** Nice round ticks covering [min, max] (about `count` of them), and the domain they span. */
export function niceTicks(min: number, max: number, count = 4): { ticks: number[]; lo: number; hi: number } {
  if (!Number.isFinite(min) || !Number.isFinite(max)) return { ticks: [0, 1], lo: 0, hi: 1 }
  if (min === max) {
    const pad = Math.abs(min) || 1
    return niceTicks(min - pad, max + pad, count)
  }
  const raw = (max - min) / count
  const magnitude = 10 ** Math.floor(Math.log10(raw))
  // The nearest nice step at or a little under the even split: a few more ticks beat a mostly empty top band.
  const step = [1, 2, 2.5, 5, 10].map((m) => m * magnitude).find((s) => s >= raw * 0.75)!
  const lo = Math.floor(min / step) * step
  const hi = Math.ceil(max / step) * step
  const ticks: number[] = []
  // Counted, not stepped: at extreme values adding a step can change nothing, and the loop would never end.
  const steps = Math.min(Math.round((hi - lo) / step), MAX_TICKS)
  for (let i = 0; i <= steps; i++) ticks.push(Number((lo + i * step).toPrecision(12)))
  return { ticks, lo, hi }
}

/** The y domain: from zero for bars (lengths compare) and when it's close by; else around the data. */
export function yDomain(rep: Pick<ChartData, 'chart' | 'series'>) {
  const ys = rep.series.flatMap((s) => s.points.map((p) => p.y))
  const min = Math.min(...ys)
  const max = Math.max(...ys)
  const fromZero = rep.chart === 'bar' || (min >= 0 && min <= max * 0.35)
  return niceTicks(fromZero ? Math.min(0, min) : min, max)
}

/** The categories of a bar chart, in the order they first appear. */
export function categories(series: readonly ChartSeries[]): string[] {
  const out: string[] = []
  for (const s of series) for (const p of s.points) if (!out.includes(String(p.x))) out.push(String(p.x))
  return out
}

/** The x domain of a line chart. */
export function xDomain(series: readonly ChartSeries[]) {
  const xs = series.flatMap((s) => s.points.map((p) => p.x as number))
  return niceTicks(Math.min(...xs), Math.max(...xs), 5)
}

/** On a line chart, each series' point nearest to `x` (for the crosshair's tooltip), lead first. */
export function valuesAt(
  series: readonly ChartSeries[],
  x: number,
  styles: Map<string, SeriesStyle>
): { name: string; x: number; y: number }[] {
  const out: { name: string; x: number; y: number }[] = []
  for (const s of series) {
    let best: { x: number; y: number } | null = null
    for (const p of s.points)
      if (!best || Math.abs((p.x as number) - x) < Math.abs(best.x - x)) best = { x: p.x as number, y: p.y }
    if (best) out.push({ name: s.name, ...best })
  }
  return out.sort((a, b) => Number(!!styles.get(b.name)?.lead) - Number(!!styles.get(a.name)?.lead))
}

const ISO_TIME = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}/
const dayFormat = new Intl.DateTimeFormat('en', { month: 'short', day: 'numeric' })
const timeFormat = new Intl.DateTimeFormat('en', { hour: 'numeric', minute: '2-digit' })
const fullFormat = new Intl.DateTimeFormat('en', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })

/**
 * How bar categories read: ISO times become "Oct 14" (or "9:05 AM" when they're all one day); anything else as it is.
 * `full` for a tooltip or a table: "Oct 14, 9:05 AM".
 */
export function categoryLabels(cats: readonly string[]): {
  short: (cat: string) => string
  full: (cat: string) => string
} {
  const times = cats.length > 0 && cats.every((c) => ISO_TIME.test(c) && !Number.isNaN(Date.parse(c)))
  if (!times) return { short: (c) => c, full: (c) => c }
  const oneDay = new Set(cats.map((c) => dayFormat.format(new Date(c)))).size === 1
  return {
    short: (c) => (oneDay ? timeFormat : dayFormat).format(new Date(c)),
    full: (c) => fullFormat.format(new Date(c)),
  }
}

/** The most category labels a bar chart writes under itself; past that, every nth (the rest are in the hover and the table). */
export const MAX_CATEGORY_LABELS = 8
